import ShadowCard from 'components/common/cards/ShadowCard';
import Price from 'components/common/Price';
import { useProducts } from 'contexts/ProductContext';
import { useSubscriptions } from 'contexts/SubscriptionContext';
import ReviewOrderModal from './ReviewOrderModal';
import useModal from 'hooks/useModal';
import { Typography, Stack, Link, Box, Badge, Divider } from '@newulife/common';
import formatImageSrc from 'helpers/formatImageSrc';
import { getShopifyDisplayProducts } from 'helpers/shopifyHelpers';
import { useField } from 'hooks/useContentfulApi';

export default function ReviewOrder({ isShopifyUser }) {
  const [pageData] = useField('banner1Ref');
  const [modalData] = useField('banner2Ref');
  const { state, getTotalProductPrice } = useProducts();
  const { state: subState, getTotalSubscriptionPrice } = useSubscriptions();
  const { Modal, openModal } = useModal();

  const {
    text1: orderSummary,
    text2: todaysOrder,
    text3: subscriptionOrder,
    text4: reviewOrder,
  } = pageData?.fields;

  const productTotal = getTotalProductPrice();
  const subscriptionTotal = getTotalSubscriptionPrice();
  const shopifyDisplay = getShopifyDisplayProducts(
    state?.products || [],
    subState?.subscriptions || [],
  );
  const allProducts = (state?.starterKit ? [state?.starterKit] : []).concat(shopifyDisplay || []);

  if (isShopifyUser) {
    if (!allProducts.length) return null;
    return (
      <Stack data-testid={`${orderSummary?.replace(/\W/g, '')}-section`} width="100%" pb={4}>
        <Typography
          variant="body3"
          textAlign="center"
          mb={2}
          sx={{
            textTransform: 'capitalize',
          }}
        >
          {orderSummary}
        </Typography>
        <div className="order-summary-card">
          <Stack pt={2} display="grid" gap={2} maxWidth="600px" mx="auto">
            <Stack display="grid" gap={2}>
              {allProducts?.map((o) => (
                <Stack
                  key={o.ProductID}
                  display="flex"
                  direction="row"
                  justifyContent="space-between"
                  gap={2}
                  width="100%"
                  alignItems="center"
                >
                  <Stack
                    display="inline-grid"
                    justifyItems="flex-start"
                    alignItems="center"
                    gridTemplateColumns={{
                      sm: 'auto 1fr',
                    }}
                    gap={2}
                  >
                    <Badge
                      badgeContent={o.Quantity}
                      sx={{
                        '.MuiBadge-badge': {
                          background: 'rgb(125 125 125)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #0f100f',
                          img: {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          },
                        }}
                      >
                        <img src={formatImageSrc(o.SmallImageURL)} alt="product image" />
                      </Box>
                    </Badge>
                    <Stack gap={0.5}>
                      <Typography variant="body1">{o.DisplayName}</Typography>
                      <Typography
                        variant="label1"
                        sx={{
                          opacity: 0.7,
                        }}
                      >
                        {!o.isAutoship || o.IsStarterKit ? 'One Time' : 'Subscribe & Save'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack
                    display="inline-grid"
                    gridTemplateColumns={{
                      sm: 'repeat(2, 80px)',
                    }}
                    textAlign="right"
                    gap={2}
                  >
                    {!!o.WholesalePrice ? (
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{
                          opacity: 0.7,
                          textDecoration: 'line-through',
                        }}
                      >
                        {o.CurrencySymbol}
                        {(o.RetailPrice * o.Quantity)?.toFixed(2)}
                      </Typography>
                    ) : (
                      <div />
                    )}
                    <Typography variant="body2" fontWeight="bold">
                      {o.CurrencySymbol}
                      {((o.WholesalePrice || o.Price) * o.Quantity)?.toFixed(2)}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <Divider />
            <Stack gap={0.5}>
              {!!+productTotal && <ReviewLine title={todaysOrder} total={productTotal} />}
              {subState.subscriptions.length !== 0 && (
                <ReviewLine title={subscriptionOrder} total={subscriptionTotal} />
              )}
            </Stack>
          </Stack>
        </div>
      </Stack>
    );
  }

  return (
    <>
      <div
        className="order-summary-card-container form-max"
        data-testid={`${orderSummary?.replace(/\W/g, '')}-section`}
      >
        <div className="order-summary-card-title">{orderSummary}</div>
        <div className="order-summary-card">
          <Stack
            gap={0.5}
            sx={{
              padding: '16px 16px 12px',
              borderRadius: '0px 0px 12px 12px',
              border: '1px solid #0f100f',
            }}
          >
            <ReviewLine title={todaysOrder} total={productTotal} />
            {subState.subscriptions.length !== 0 && (
              <ReviewLine title={subscriptionOrder} total={subscriptionTotal} />
            )}
            <Link
              datatestid="reviewOrder"
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                py: 1,
              }}
              underline="hover"
              onClick={() => openModal(true)}
            >
              {reviewOrder}
            </Link>
          </Stack>
        </div>
      </div>
      <Modal buttonText={modalData.fields.buttonText}>
        <ReviewOrderModal data={modalData} />
      </Modal>
    </>
  );
}

const ReviewLine = ({ title, total }) => {
  return (
    <Stack
      display="grid"
      gridAutoFlow="column"
      alignItems="center"
      alignContent="center"
      justifyContent="space-between"
    >
      <Typography variant="subhead2">{title}</Typography>
      <Typography data-testid={`${title?.replace(/\W/g, '')}-orderSummary`} variant="header2">
        <strong>
          <Price value={total} />
        </strong>
      </Typography>
    </Stack>
  );
};
